import styles from "../../../stylesheets/typography.module.scss"
import {classNames} from "scholastic-client-components/src/SharedPackages/classnames"
import React from "react"
import {PolicyHeader} from "../PolicyHeader"

export const GeneralPolicyHeader: React.FC<{small?: boolean}> = ({small}) => (
    <PolicyHeader small={small} title={"TSN Visual Communication Solutions, LLC. Privacy Policy"} />
)

export const GeneralPolicyBody = () => (
    <>
        <h3 className={styles.h3}>
            <strong>EFFECTIVE DATE:</strong> November 1, 2024
        </h3>
        <p className={styles.p}>
            Thank you for choosing online products and services owned and operated by TSN Visual
            Communication Solutions, LLC (hereafter called &quot;
            <strong>TSN</strong>&quot;). This Privacy Policy (this &quot;
            <strong>Policy</strong>&quot;) outlines how TSN collects, uses, discloses, and
            safeguards Personal Information (defined below) in connection with TSN’s online and/or
            mobile services, websites, software, and content assets (collectively, the &quot;
            <strong>Services</strong>&quot;), including the Services provided through the following
            sites (collectively, the &quot;<strong>Sites</strong>&quot;):
        </p>
        <ul className={styles.ul}>
            <li className={styles.li}>
                <a target={"_blank"} href={"http://www.tsnvcs.com"} rel="noreferrer">
                    www.tsnvcs.com
                </a>
            </li>
            <li className={styles.li}>
                <a
                    target={"_blank"}
                    href={"https://portal.thescholasticnetwork.com"}
                    rel="noreferrer"
                >
                    portal.thescholasticnetwork.com
                </a>
            </li>
            <li className={styles.li}>
                <a target={"_blank"} href={"https://portal.tsnvcs.com"} rel="noreferrer">
                    portal.tsnvcs.com
                </a>
            </li>
        </ul>
        <p className={styles.p}>
            This Policy is meant to be reviewed in conjunction with the{" "}
            <strong>TSN Terms of Service</strong> available{" "}
            <a
                target={"_blank"}
                href={"https://portal.thescholasticnetwork.com/auth/terms_of_use"}
                rel="noreferrer"
            >
                here
            </a>
            . These terms also govern any and all applications associated with the above or any
            other TSN website, app, or online service that links to these Terms (the &quot;Terms of
            Service&quot;). Capitalized terms that are used but not defined in this Policy have the
            meanings given such terms in the Terms of Service.
        </p>

        <h2 className={styles.h2}>1. General</h2>
        <p className={styles.p}>
            TSN (&quot;<strong>we</strong>&quot;, &quot;<strong>us</strong>&quot;, or &quot;
            <strong>our</strong>&quot;) respects the privacy of everyone that uses the Services. As
            a user of the Services, you have a right to know how TSN collects, uses, discloses, and
            safeguards your Personal Information and the rights you have in relation to your
            Personal Information. For purposes of this Policy, &quot;
            <strong>Personal Information</strong>&quot; means information that identifies, relates
            to, describes, is reasonably capable of being associated with, or could reasonably be
            linked, directly or indirectly, with a particular individual or household. However,
            &quot;Personal Information&quot; does not include anonymous, aggregated, or other
            non-personally identifiable information (i.e., information that cannot be associated
            with or traced back to a specific individual or household)
        </p>
        <p className={styles.p}>
            The Sites are hosted in the United States and are subject to U.S. state and federal law.
            If you are accessing the Services from other jurisdictions, please be advised that you
            are transferring your Personal Information to us in the United States, and by using the
            Services, you are agreeing to that transfer and use of your Personal Information in
            accordance with this Policy. You also agree to abide by the applicable laws of the State
            of Texas and U.S. federal law concerning your use of the Services and your agreements
            with us as stipulated in the TSN Terms of Service. If your use of the Services would be
            unlawful in your jurisdiction, please do not use the Services.
        </p>

        <h2 className={styles.h2}>2. How TSN Collects and Uses Information</h2>
        <h4 className={styles.h4}>A. Subscriber Information</h4>
        <p className={styles.p}>
            When you subscribe to the Services, TSN will ask you to provide certain Personal
            Information and other information about you and, if applicable, information about your
            organization (&quot;<strong>Subscriber Information</strong>&quot;). TSN collects
            Subscriber Information directly from you. For example, if you purchase a license to use
            the Services on behalf of a school or other organization, TSN will collect information
            about you and your organization, including your name, your email address, your role
            within your organization, your organization’s name, your organization’s physical
            address, and payment information for your organization.
        </p>
        <p className={styles.p}>
            TSN uses your Subscriber Information to set up and administer your TSN account, provide
            the Services for you and your Users (as defined below), support and develop the
            Services, provide you and your Users with information and updates about the Services and
            your TSN account, communicate with you about billing obligations, and process payments.
        </p>
        <h4 className={styles.h4}>B. User Information</h4>
        <p className={styles.p}>
            If your organization (district, school, college, university, business, etc.) subscribes
            to the Services, your organization will have at least one, and sometimes many,
            individuals registered as users of its TSN account (each, a &quot;
            <strong>User</strong>&quot;). TSN collects information, including Personal Information,
            when individuals register as Users of your organization’s TSN account and thereafter
            when Users access the Services (&quot;
            <strong>User Information</strong>&quot;). For example, if you are registering as a User
            of your organization’s TSN account, we will ask you to provide your name, email address,
            organization, role within your organization (student, teacher, administrator, etc.), and
            other information we may need in order to activate and maintain your User account. TSN
            collects User Information directly from individual Users and, from time to time, from
            the User’s organization and account administrator(s).
        </p>
        <p className={styles.p}>
            TSN uses User Information to contact Users from time to time in connection with the
            Services (such as to address issues or complaints related to a User’s use of TSN or any
            of the Services), provide Users with information about TSN or the Services, verify
            Users’ identities or login credentials, provide legal and other notices to Users, and
            notify Users regarding potential violations of TSN’s terms and policies.
        </p>
        <h4 className={styles.h4}>C. Content Information</h4>
        <p className={styles.p}>
            As a TSN subscriber, you and your Users may voluntarily upload certain information to
            the Services (&quot;<strong>Content Information</strong>&quot;). For example, student
            Users may upload assignments into the Services as directed by their teachers, and school
            administrators may upload information into the Services in order to communicate that
            information with other Users of the school’s TSN account. TSN does not monitor or
            control Content Information; you and your Users determine what information will be
            uploaded into the Services through your TSN account, which of your Users will have
            access to that content, and if and when that content will be removed from the Services.
            You, as the TSN account owner, are responsible for managing Content Information entered
            into the Services through your TSN account and for taking all measures, and obtaining
            all consents, necessary to comply with applicable privacy laws to ensure that TSN may
            lawfully (i) share Content Information as directed by you and your Users, and (ii)
            otherwise process, use, and disclose Content Information to provide the Services to you
            and your Users.
        </p>
        <p className={styles.p}>
            TSN uses Content Information only as required to perform the Services for you and your
            Users. TSN functionality allows Users to control their Content Information, including
            which other Users may access or use their Content Information. TSN will only use and
            share Content Information as you and your Users direct using functionality and controls
            within the Services. You and your Users are solely responsible for controlling what
            Content Information is entered into the Services through your TSN account and how and
            with whom that Content Information is used and shared with other Users.
        </p>
        <p className={styles.p}>
            NOTE: TSN Services enable you to broadcast digital signage content to display units
            (TVs, flat panels, interactive panels, websites, live streams, etc.) under your control.
            TSN Services do <u>not include</u> a mechanism for in-platform individual communication
            between platform users with one exception. If, and only if, you permit end-users to use
            the Student Projects module, admin users of that module may communicate with end-users
            (usually students). Students may not communicate (no feature exists nor is planned) with
            each other from within the TSN Services.
        </p>
        <h4 className={styles.h4}>D. Automatically Collected Information</h4>
        <p className={styles.p}>
            Like many websites and online services, TSN automatically collects information when you
            and your Users access the Services, read our emails, or otherwise engage with TSN and
            its personnel (&quot;Automatically Collected Information&quot;). Automatically Collected
            Information is typically collected through a variety of tracking technologies, including
            cookies, log files, and similar technology (collectively &quot;Tracking
            Technologies&quot;). These Tracking Technologies collect information about how you
            access and use the Services (e.g., referring/exit pages and URLS, how frequently you
            access the Services, the pages you view, the links you click, and other actions you take
            within the Services); information about your browser and information about the device(s)
            you use to access the Services (e.g., Internet Protocol (IP address), browser type,
            browser language, Internet service provider, device type, model and manufacturer,
            hardware ID, Android SDK Version, LAN IP, a unique ID that allows us to uniquely
            identify your browser, device or account, operating system brand and model, and whether
            you access the Services from multiple devices). We may also collect information about
            your geographical location data at the city level. We may collect analytics data, or use
            third-party analytics tools, such as Google Analytics, to help us measure traffic and
            usage trends for the Services and to understand more about the demographics and
            behaviors of our users. You can learn more about Google&apos;s practices at{" "}
            <a
                href={"http://www.google.com/policies/privacy/partners"}
                target={"_blank"}
                rel="noreferrer"
            >
                http://www.google.com/policies/privacy/partners
            </a>
            . We may also work with third party partners to employ technologies, including the
            application of statistical modeling tools, which permit us to recognize and contact you
            across multiple devices. Although we do our best to honor the privacy preferences of our
            users, we are unable to respond to Do Not Track signals set by your browser at this
            time.
        </p>
        <p className={styles.p}>
            We use or may use the data collected through tracking technologies to secure the
            Services, to improve the Services, to save you time, to provide better technical
            support, for promotional purposes, and to track website usage. For example, tracking
            technologies help us to:
            <ul className={styles.ul}>
                <li className={styles.li}>
                    Keep track of whether you are signed in or have previously signed in so that we
                    can display all the features that are available to you.
                </li>
                <li className={styles.li}>
                    Remember your settings on the pages you visit, so that we can display your
                    preferred content the next time you visit.
                </li>
                <li className={styles.li}>
                    Customize the function and appearance of the pages you visit based on
                    information relating to your account; for example, to default you to a
                    particular access level.
                </li>
                <li className={styles.li}>
                    Track website usage for various purposes including website optimization, website
                    improvement, sales, marketing, and billing.
                </li>
            </ul>
        </p>
        <p className={styles.p}>
            Most browsers are initially set up to accept cookies, but you can reset your browser to
            refuse all cookies or to indicate when a cookie is being sent. However, some features
            and services (particularly those that require you to sign in) may not function properly
            if your cookies are disabled. You may also set your email options to prevent the
            automatic downloading of images that may contain technologies that would allow us to
            know whether you access our email and perform certain functions with it. Deleting
            cookies does not prevent the collection of information through non-cookie technologies
            and does delete Local Storage Objects (LSOs) such as Flash objects and HTML5. You can
            learn more about Flash objects — including how to manage privacy and storage settings
            for Flash cookies — on{" "}
            <a target={"_blank"} href={"https://www.adobe.com/"} rel="noreferrer">
                Adobe&apos;s website
            </a>
            . If you choose to delete Flash objects from our sites, then you may not be able to
            access and use all or part of the sites or benefit from the information and services
            offered.
        </p>

        <h4 className={styles.h4}>E. Permissions</h4>
        <p className={styles.p}>
            TSN uses permissions to assist with the delivery of services and support for your usage
            of the TSN Services:
            <ul className={styles.ul}>
                <li className={styles.li}>
                    CHANGE_WIFI_STATE: This permission allows the app to reconnect to Wi-Fi in case
                    of a connection loss.
                </li>
                <li className={styles.li}>
                    RECEIVE_BOOT_COMPLETED: This enables the app to start running in the background
                    as soon as the device boots up. This permission is required to run the Services
                    when the device is turned on. The app can either run content or wait for a start
                    of a period, alert, live stream, etc.
                </li>
                <li className={styles.li}>
                    FOREGROUND_SERVICE and FOREGROUND_DATA_SYNC allows the app to receive data
                    updates - either scheduled updates, alerts going down, starts of streams, etc.
                </li>
                <li className={styles.li}>
                    SYSTEM_ALERT_WINDOW allows the app to display information over other apps. This
                    permission is used to display Services whenever the Services should appear in
                    the foreground. When a time period starts or an alert occurs, etc.
                </li>
            </ul>
        </p>

        <h2 className={styles.h2}>3. Information TSN Never Collects</h2>
        <p className={styles.p}>
            The Services do not gather, store, or require information related to any of the
            following:
            <ul className={styles.ul}>
                <li className={styles.li}>
                    Student data beyond the required information stated in Section 2 above.
                </li>
                <li className={styles.li}>
                    Employee data beyond the required information stated in Section 2 above.
                </li>
                <li className={styles.li}>
                    Any other personally identifiable information (PII) other than that stipulated
                    in Section 2 above.
                </li>
            </ul>
        </p>

        <h2 className={styles.h2}>4. Use of Collected Information</h2>
        <p className={styles.p}>
            TSN uses the information we collect for the following purposes:
            <ul className={styles.ul}>
                <li className={styles.li}>
                    <strong>To provide and maintain the Services.</strong> We use the information we
                    collect to deliver the Services to you and your Users. For example, we use your
                    information to give you access to the appropriate licensed account, process
                    payments, or provide you with ongoing support for your use of the Service.
                </li>
                <li className={styles.li}>
                    <strong>To improve, personalize, and develop the Services.</strong> We use
                    information to tailor the content and information that we may send or display to
                    users, to offer personalized content and instructions, and to otherwise
                    personalize your experience while using the Services, including the various
                    devices you may use to access the Services. We collect statistics to better
                    understand how users access and use our Services, monitor the effectiveness of
                    our Services, detect usage patterns, and to diagnose or fix technology problems.
                    We also use information to demonstrate the effectiveness of the Services,
                    perform research, and to develop, support, and improve the Services.
                </li>
                <li className={styles.li}>
                    <strong>To communicate with you.</strong> We use your information to provide
                    transactional notifications for certain activities relating to your use of the
                    Services. For example, we may send email notifications to highlight new
                    features, to provide receipt for payment or other subscription notices. From
                    time to time, we may send periodic promotional or informational emails to you
                    and your users. We do not use Student Data to send marketing communications, and
                    we do not send marketing communications to student Users. You may opt-out of
                    certain communications (e.g., marketing or certain notifications about your use
                    of the Services) by following the opt-out instructions contained in the email.
                    You may not opt out of service-related communications (e.g., account
                    verification, transactional communications, changes/updates to features of the
                    Services, technical and security notices).
                </li>
                <li className={styles.li}>
                    <strong>To promote safety and security and respond to legal requests.</strong>{" "}
                    We use information to promote the safety and security of the Services, our uses
                    and other third parties. For example, we may use the information to authenticate
                    users, facilitate secure payments, detect and prevent fraud and other harmful
                    activities, respond to legal requests or claims, and enforce our terms and
                    policies.
                </li>
            </ul>
        </p>

        <h2 className={styles.h2}>5. Sharing of Collected Information</h2>
        <p className={styles.p}>
            TSN may share or disclose your personal information as needed to provide the Services or
            with your consent/permission. We may also share personal information in the
            circumstances below.
        </p>
        <h4 className={styles.h4}>A. Sharing with Third Parties</h4>
        <p className={styles.p}>
            We may share information when you direct us to share such information with another
            party. We may also share information with third-party service providers, for example,
            (i) if you authorize Google login, similar authentication tools for your account; (ii)
            if you authorize Canva to share your Canva content directly to the Services; or, (iii)
            you subscribe to TSN Premium Content inside of the Services.
        </p>
        <h4 className={styles.h4}>B. Others within your Organization</h4>
        <p className={styles.p}>
            TSN may share information collected from users associated with an organization with
            other users or persons designated by that organization, such as teachers and school
            administrators, business leaders, or HR professionals. Messages and content shared or
            posted by members of an organizational community, such as messages between a teacher and
            students in a class or managers and their direct reports will be accessible by account
            administrators or other designated organizational users.
        </p>
        <h4 className={styles.h4}>C. Vendors and Service Providers</h4>
        <p className={styles.p}>
            TSN may share information with our trusted vendors, third party service providers and
            individuals to provide services or products for us or on our behalf, which may include
            analytics, hosting, billing, targeted advertising, and marketing (provided however, that
            TSN will not knowingly use any Student Data to target advertising or marketing). Vendors
            and service providers who have access to Student Data, if any, shall be contractually
            bound to uphold privacy and confidentiality terms no less protective than those provided
            herein.
        </p>
        <h4 className={styles.h4}>D. Aggregate or De-Identified Information</h4>
        <p className={styles.p}>
            We may share information in aggregated and/or anonymous form that cannot reasonably be
            used to identify an individual. For example, TSN may disclose aggregated user statistics
            (e.g., the total number or percentage of Users of the Services from a particular
            geographic region) in order to describe the Services to current and prospective partners
            and other third parties, and for other lawful purposes.
        </p>
        <h4 className={styles.h4}>E. Merger or Sales</h4>
        <p className={styles.p}>
            If TSN becomes involved in a merger, acquisition, bankruptcy, change of control, or any
            form of sale of some or all of its assets, your personal information may be transferred
            or disclosed in connection with the business transaction. In such an event, we will make
            efforts to provide notice before personal information is transferred and becomes subject
            to a different privacy policy. We will not transfer Student Data associated with School
            accounts in the event of a merger or sale unless the recipient has committed to
            similarly stringent privacy protections as described herein, or we will provide Schools
            with notice and an opportunity to opt-out of the transfer of Student Data by terminating
            their accounts and any associated student users before the transfer occurs.
        </p>
        <h4 className={styles.h4}>F. Other</h4>
        <p className={styles.p}>
            TSN may release personal information if it has a good faith belief that access, use,
            preservation, or disclosure of such information is reasonably necessary to (a) satisfy
            any applicable law, regulation, legal process, or enforceable governmental request; (b)
            enforce applicable Terms of Service, including investigation of potential violations
            thereof; (c) detect, prevent or otherwise address fraud, security or technical issues;
            (d) protect the rights, property, or personal safety of TSN, its users, or the public;
            or (e) as required or permitted by law.
        </p>

        <h2 className={styles.h2}>6. Third-Party Tracking for Online Advertising</h2>
        <p className={styles.p}>TSN does not display any targeted advertising on the Services.</p>

        <h2 className={styles.h2}>7. Email Communications</h2>
        <p className={styles.p}>
            TSN may send you email notifications regarding our products and services, or your use of
            our products and services. Only TSN (or its vendors or service providers operating on
            its behalf) will send you these emails. You can choose not to receive these emails by
            clicking the unsubscribe link in any email or by contacting{" "}
            <a href={"mailto:support@tsnvcs.com"}>support@tsnvcs.com</a>. Please note that you are
            not permitted to unsubscribe or opt-out of non-promotional messages regarding your
            account, such as account verification, changes or updates to features of the Services,
            or technical or security notices.
        </p>

        <h2 className={styles.h2}>8. Accessing and Updating your Personal Information</h2>
        <p className={styles.p}>
            You may edit your account information at any time by signing in to your Services
            account. In the Network, click on the Account Menu in the upper right corner and click
            on your name. You may change your name, email address, and password. Other information
            may be changed by an authorized user who has been granted administrative rights over
            your account.
        </p>
        <p className={styles.p}>
            If you do not provide and maintain accurate information for your account, TSN may not be
            able to provide you with notices set forth in this Privacy Policy or in our Terms of
            Service. You are solely responsible for the accuracy of all of your information.
        </p>
        <p className={styles.p}>
            If you are an educational institution using the Services and providing students with
            their own account access (i.e. as student content creators), please refer all questions
            and requests regarding access, modification, or deletion of a student user&apos;s
            information to the appropriate Service account administrator for your account/license.
            Should the educational institution need assistance with such requests, they may contact
            TSN at <a href={"mailto:support@tsnvcs.com"}>support@tsnvcs.com</a>.
        </p>

        <h2 className={styles.h2}>9. Protection and Retention of Information</h2>
        <h4 className={styles.h4}>A. Retention of Personal Information</h4>
        <p className={styles.p}>
            TSN will retain personal information for as long as needed to provide the Services and
            for our internal business purposes, which may extend beyond the termination or
            cancellation of your subscription or user account. For example, we may retain certain
            data as necessary to prevent fraud or future abuse, for recordkeeping or other
            legitimate business purposes, or if required by law. We may also retain and use
            information which has been de-identified or aggregated such that it can no longer
            reasonably identify a particular individual. All retained personal information will
            remain subject to the terms of this Privacy Policy.
        </p>
        <p className={styles.p}>
            Student Data. We will not knowingly retain Student Data beyond the time period required
            to support an educational purpose, unless authorized by an educational institution. We
            do not delete or de-identify any Student Data from an active student user account
            associated with a School except at the direction of the School. The School is
            responsible for maintaining current student rosters and identifying Student Data which
            the School no longer needs for an educational purpose by removing students from your
            account.
        </p>
        <p className={styles.p}>
            Unless otherwise directed by you, we will delete or de-identify personal information of
            student and child users after a period of inactivity, after the termination or
            cancellation of the license subscription, or after termination of our agreement with
            you. Authorized Service administrators may contact us at{" "}
            <a href={"mailto:support@tsnvcs.com"}>support@tsnvcs.com</a> with any additional
            retention questions.
        </p>
        <p className={styles.p}>
            We may not be able to immediately or completely delete all data in all instances, such
            as information retained in technical support records, customer service records, backups,
            and other similar business records. We will not be required to delete any information
            which has been de-identified or disassociated with personal identifiers such that the
            remaining information cannot reasonably be used to identify a particular individual.
        </p>
        <h4 className={styles.h4}>B. Protection of Personal Information</h4>
        <p className={styles.p}>
            The protection and security of your personal information is very important to TSN. We
            have implemented a variety of physical, administrative and technological safeguards
            designed to preserve the integrity and security of the personal information we collect
            and to protect against unauthorized access to data. These include internal reviews of
            our data collection, storage, and processing practices and security measures. We
            restrict access to personal information to TSN employees, contractors, and agents who
            need to know that information in order to operate, develop, or improve our Services. Our
            employees may be subject to disciplinary action, including termination, if they fail to
            meet privacy and confidentiality obligations. However, no security system is
            impenetrable—for that reason, we cannot guarantee the security of your personal
            information. If personal information under our control is compromised as a result of a
            breach of security, we will take reasonable steps to investigate the situation and take
            all steps required by applicable laws and regulations.
        </p>
        <p className={styles.p}>
            Maintaining the security of your personal information also requires your cooperation and
            involvement. For your protection, remember to sign out of all accounts before closing
            your browser. There is a sign out link available on most TSN Network pages and Service
            pages that require a username and password prior to accessing such pages. After signing
            out, make sure to close all browser windows. In addition, do not use the
            &quot;Remember&quot; feature if you are signing in to your account from a computer that
            other people may have access to. This is to ensure that others cannot access your
            personal information if you share a computer with someone else or are using a public
            computer.
        </p>
        <p className={styles.p}>
            If you have any concerns about the security of your account or the security of the
            Service, we ask that you report your concern to us immediately at{" "}
            <a href={"mailto:support@tsnvcs.com"}>support@tsnvcs.com</a>. We appreciate your
            feedback and take all reported concerns seriously.
        </p>
        <h4 className={styles.h4}>C. Data storage and transfer</h4>
        <p className={styles.p}>
            TSN is located in the United States. Personal information collected through our Website
            and Service may be stored and processed in the United States or any other country in
            which TSN or its affiliates or service providers maintain facilities.
        </p>

        <h4 className={styles.h4}>D. European Economic Area (&quot;EEA&quot;) Requirements</h4>
        <p className={styles.p}>
            For personal information subject to the European Union General Data Processing
            Regulation (&quot;GDPR&quot;), we rely on several legal bases to process the data. These
            legal bases include where:
            <ul className={styles.ul}>
                <li className={styles.li}>
                    The processing is necessary to perform our contractual obligations in our Terms
                    of Service or other contracts with you (such as to provide you the Service as
                    described in our Terms of Service);
                </li>
                <li className={styles.li}>
                    You have given your prior consent, which you may withdraw at any time (such as
                    for marketing purposes or other purposes we obtain your consent for from time to
                    time);
                </li>
                <li className={styles.li}>
                    The processing is necessary to comply with a legal obligation, a court order or
                    to exercise or defend legal claims;
                </li>
                <li className={styles.li}>
                    The processing is necessary for the purposes of our legitimate interests, such
                    as in improving, personalizing, and developing the Service, marketing the
                    Service, such as new features or products that may be of interest, and promoting
                    safety and security as described above.
                </li>
            </ul>
        </p>
        <p className={styles.p}>
            If you have any questions about or would like further information concerning the legal
            basis on which we collect and use your personal information, please contact us at{" "}
            <a href={"mailto:support@tsnvcs.com"}>support@tsnvcs.com</a> or{" "}
            <a href={"mailto:legal@tsnvcs.com"}>legal@tsnvcs.com</a>.
        </p>
        <p className={styles.p}>
            Residents in the EEA are entitled to certain rights with respect to personal information
            that we hold about them under the GDPR:
        </p>
        <ul className={styles.ul}>
            <li className={styles.li}>
                <strong>Right of access and portability</strong>. The right to obtain access to your
                personal information, along with certain related information, and to receive that
                information in a commonly used format and to have it transferred to another data
                controller;
            </li>
            <li className={styles.li}>
                <strong>Right to rectification</strong>. The right to obtain rectification of your
                personal information without undue delay where that personal information is
                inaccurate or incomplete;
            </li>
            <li className={styles.li}>
                <strong>Right to erasure</strong>. The right to obtain the erasure of your personal
                information without undue effort in relation to the purposes for which it was
                collected or processed;
            </li>
            <li className={styles.li}>
                <strong>Right to restriction</strong>. The right to obtain the restriction of the
                processing undertaken by us on your personal information in certain circumstances,
                such as where the accuracy of the personal information is contested by you, for a
                period enabling us to verify the accuracy of that personal information; and,
            </li>
            <li className={styles.li}>
                <strong>Right to object</strong>. The right to object, on grounds relating to your
                particular situation, to the processing of your personal information, and to object
                to processing of your personal information for direct marketing purposes, to the
                extent it is related to such direct marketing.
            </li>
        </ul>
        <p className={styles.p}>
            You may also have the right to make a GDPR complaint to the relevant Supervisory
            Authority. A list of Supervisory Authorities is available here:{" "}
            <a
                target={"_blank"}
                href={
                    "https://ec.europa.eu/justice/data-protection/bodies/authorities/index_en.htm"
                }
                rel="noreferrer"
            >
                https://ec.europa.eu/justice/data-protection/bodies/authorities/index_en.htm
            </a>
            . If you need further assistance regarding your rights, please contact us via email at{" "}
            <a href={"mailto:legal@tsnvcs.com"}>legal@tsnvcs.com</a>. In some cases our ability to
            uphold these rights for you may depend upon our obligations to process personal
            information for security, safety, fraud prevention reasons, compliance with regulatory
            or legal requirements, or because processing is necessary to deliver the services you
            have requested. Where this is the case, we will inform you of specific details in
            response to your request.
        </p>

        <h4 className={styles.h4}>E. Additional Information & Jurisdictional Requirements</h4>
        <p className={styles.p}>
            TSN may choose or be required to provide different or additional disclosures relating to
            the processing of personal information about you if you reside in a specific county,
            region, or state. TSN recognizes you have choices regarding your personal information.
            In some jurisdictions, the controls and choices may be enforceable as rights under
            applicable law.
            <ul className={styles.ul}>
                <li className={styles.li}>
                    You have the <strong>right to know</strong> about the personal information TSN
                    collects and how it is used and shared;
                </li>
                <li className={styles.li}>
                    You have the <strong>right to delete</strong> personal information collected
                    from TSN (with some exceptions as permitted by law);
                </li>
                <li className={styles.li}>
                    You have the <strong>right to opt-out</strong> of receiving sales/marketing
                    communications or of sharing your personal information;
                </li>
                <li className={styles.li}>
                    You have the <strong>right to correct</strong> inaccurate personal information
                    that TSN has about you; and,
                </li>
                <li className={styles.li}>
                    You have the <strong>right to limit</strong> the use and disclosure of sensitive
                    personal information.
                </li>
            </ul>
        </p>
        <p className={styles.p}>
            To exercise any of your rights, please contact your Service administrator or{" "}
            <a href={"mailto:legal@tsnvcs.com"}>legal@tsnvcs.com</a>. TSN will need to verify your
            identity before processing your request and/or require you to log into your account, if
            you have one. In certain circumstances, we may decline or limit your request,
            particularly where we are unable to verify your identity or locate your information in
            our systems, or as permitted by law.
        </p>
        <p className={styles.p}>
            To discuss your rights under specific laws applicable to your country, region, or state,
            please contact us at <a href={"mailto:legal@tsnvcs.com"}>legal@tsnvcs.com</a>. In the
            subject line of the email, please include &quot;Rights Request for&quot; along with the
            name of the specific jurisdiction for which you’re inquiring.
        </p>

        <h4 className={styles.h4}>F. Protection of Students’ and Children’s Data</h4>
        <p className={styles.p}>
            TSN does not permit students and children to create their own accounts and does not
            knowingly collect personal identifying information from children under the age of 13
            without the consent and at the direction of educational institutions. If you believe any
            TSN Service inadvertently holds information from a child under 13 without parental or
            institutional consent, please contact us at{" "}
            <a href={"mailto:legal@tsnvcs.com"}>legal@tsnvcs.com</a> as soon as possible.
        </p>
        <p className={styles.p}>
            If you are an educational institution or business, you may authorize TSN to collect
            information from a child under 13. You are responsible for obtaining and maintaining all
            necessary consents from the child’s parents or guardians.
        </p>
        <p className={styles.p}>
            When TSN provides the Service to you and you provide children under the age of 13 with a
            user account to the Service, our collection, use and disclosure of children&apos;s data
            is governed by our Terms of Service and/or any other agreement with you, by the
            provisions of the Family Educational Rights and Privacy Act (&quot;FERPA&quot;), the
            Children&apos;s Online Privacy Protection Act (&quot;COPPA&quot;) and other applicable
            laws that may relate to the collection and use of personal information of
            children/students. If you have any questions about the use of children&apos;s and/or
            students&apos; data, contact us at{" "}
            <a href={"mailto:legal@tsnvcs.com"}>legal@tsnvcs.com</a>. To modify, delete, or review
            this data, please contact your local Service administrator.
            <ul className={styles.ul}>
                <li className={styles.li}>
                    <strong>The Family Educational Rights and Privacy Act</strong>{" "}
                    (&quot;FERPA&quot;). This Privacy Policy and our Service are designed to meet
                    our responsibilities to protect personal information from the students&apos;
                    educational records under FERPA. We agree to work with you to jointly ensure
                    compliance with the FERPA regulations.
                </li>
                <li className={styles.li}>
                    <strong>The Children&apos;s Online Privacy Protection Act</strong>{" "}
                    (&quot;COPPA&quot;). This Privacy Policy and our Service are designed to comply
                    with COPPA. We do not knowingly collect personal information from a child under
                    13 unless and until you authorize us to collect such information through the
                    provision of the Service on your behalf. When an educational institution uses
                    our Service in the classroom or in an educational context, we rely on that
                    institution to provide appropriate consent and authorization for a student under
                    13 to use the Service and for TSN to collect personal information from such
                    student, as permitted by COPPA. Upon request, we will provide you with the
                    opportunity to review and delete the personal information collected from your
                    children/students. If you are a parent and you have questions about your
                    child&apos;s use of our Service and any information collected, you should
                    discuss your questions with your child&apos;s educational institution.
                </li>
                <li className={styles.li}>
                    <strong>Students Online Personal Information Protection Act</strong>{" "}
                    (&quot;SOPIPA&quot;). This Privacy Policy and our Service are designed to comply
                    with SOPIPA. We do not use Student Data for targeted advertising purposes. We do
                    not use collected information to amass a profile of a K-12 student except in
                    furtherance of K-12 purposes. We never sell Student Data unless the sale is part
                    of a corporate transaction, such as a merger, acquisition, bankruptcy, or other
                    sale of assets, in which case we make efforts to ensure the successor entity
                    honors the privacy commitments made in this policy and/or we will notify you of
                    such a sale and provide you an opportunity to opt-out by deleting your account
                    before the data transfer occurs. We will not sell students&apos; personal
                    information to third parties other than in the context of a business
                    transaction.
                </li>
            </ul>
        </p>

        <h2 className={styles.h2}>10. Protection and Retention of Information</h2>
        <p className={styles.p}>
            TSN&apos;s Services may contain links to and from third-party websites of our business
            partners, advertisers, and social media sites. If you follow a link to any of these
            websites, please note that these websites have their own privacy policies and their
            practices are not covered by this Privacy Policy. We strongly recommend that you read
            their privacy policies and terms and conditions of use to understand how they collect,
            use, and share information. We are not responsible for the privacy practices or the
            content on the websites of third-party sites.
        </p>

        <h2 className={styles.h2}>11. Updates to this Privacy Policy</h2>
        <p className={styles.p}>
            TSN may, in its sole discretion, modify or update this Policy at any time, which will be
            reflected in the &apos;Last Updated&apos; date set forth at the beginning of this
            Policy. If we change this Policy in a material manner, we will do our best to notify you
            of the changes by posting a notice on our website or through other appropriate
            communication channels. Your continued use of the Services following the effective date
            of such update constitutes your acceptance of the revised Policy. If you do not agree to
            any of the terms in this Policy or to any future terms in a future revision of this
            Policy, do not use or access (or continue to access) the Service.
        </p>
        <p className={styles.p}>
            We will not make any material changes to our Privacy Policy or Terms of Service that
            would result in children/student data being used in a materially different manner than
            was disclosed when the information was collected without first giving notice to
            applicable institutions and providing a choice before such data is used in a materially
            different manner than was disclosed when the information was collected.
        </p>

        <h2 className={styles.h2}>12. Contact TSN</h2>
        <p className={styles.p}>
            If you have any questions regarding this Policy, please contact us using the information
            below. We will do our best to answer your question promptly and accurately.
        </p>
        <p className={styles.p}>
            TSN Visual Communication Solutions, LLC
            <br />
            PO Box 764
            <br />
            Blanco, TX 78606
        </p>
        <p className={styles.p}>
            800-660-5572
            <br />
            <a href={"mailto:legal@tsnvcs.com"}>legal@tsnvcs.com</a>
            <br />
            <a href={"mailto:support@tsnvcs.com"}>support@tsnvcs.com</a>
        </p>
        <h3 className={styles.h3}>
            <strong>Last Updated:</strong> November 1, 2024
        </h3>
    </>
)
